import React, { useRef, useState } from 'react';
import BaseModal from '../ui-components/BaseModal';
import useAppContext from 'flow/hooks/useAppContext';
import BaseInput from '../ui-components/BaseInput';
import BaseTextArea from '../ui-components/BaseTextArea';
import Icon from '../ui-components/Icon';
import {
    createRoomSession,
    setRoomImage,
    updateChatSession,
} from 'flow/actions/chat-actions';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { generatePath, useHistory } from 'react-router-dom';

import { routes } from 'flow/types/routes';
import { ChatSessionKey } from 'flow/types/EntityKeys';
import { roomTypes } from 'flow/pages/messages/MessageInput';
import { TypedMap } from 'flow/types/TypedMap';
import { Extension } from 'flow/types/Extension';
import { twMerge } from 'tailwind-merge';
import { List, Map, fromJS } from 'immutable';
import useExtension from 'flow/hooks/useExtension';
import useAutoFocus from 'flow/hooks/useAutoFocus';
import { getChatRoomAvatar } from '../../utils';

export interface RoomContent {
    attachment: File;
    title: string;
    description: string;
    imageSrc: string;
}

const defaultValue: RoomContent = {
    attachment: null,
    title: '',
    description: '',
    imageSrc: '',
};

interface Props {
    sessionKey?: ChatSessionKey;
    editRoom?: boolean;
    chosenExtensions?: List<TypedMap<Extension>>;
    header?: string;
    confirmButtonText?: string;
}

const ManageRoomModal = ({
    sessionKey,
    editRoom,
    chosenExtensions = List(),
    header,
    confirmButtonText,
}: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { ownExtension } = useExtension();

    const session = useAppSelector((state) =>
        state.chats.getIn(['sessions', sessionKey], Map()),
    );

    const editValue: RoomContent = {
        attachment: null,
        imageSrc: session.get('roomImageResource')
            ? getChatRoomAvatar(session?.get('roomImageResource'))
            : '',
        title: session.get('name'),
        description: session.get('roomDescription'),
    };

    const [roomContent, setRoomContent] = useState(
        editRoom ? editValue : defaultValue,
    );

    const handleAttachment = (attachment: File) => {
        if (attachment) {
            const reader = new FileReader();
            reader.readAsDataURL(attachment);

            reader.onloadend = () => {
                setRoomContent((prev) => ({
                    ...prev,
                    imageSrc: reader.result.toString(),
                    attachment,
                }));
            };
        }
    };

    const history = useHistory();

    const handleSubmit = () => {
        const onSuccess = (chatSessionKey: string) =>
            history.push(
                generatePath(routes['messages.id.settings'], {
                    id: chatSessionKey,
                }),
            );

        if (!chosenExtensions.isEmpty()) {
            chosenExtensions.push(ownExtension);
            const chatUserKeys = chosenExtensions.map((extension) =>
                Map({ key: extension.get('chatUserKey') }),
            );

            const sessionDTO = fromJS({
                name: roomContent.title,
                roomDescription: roomContent.description,
                roomType: roomTypes.room,
                members: chatUserKeys,
            });

            dispatch(
                createRoomSession(
                    sessionDTO,
                    roomContent.attachment,
                    onSuccess,
                ),
            );
        } else {
            const sessionDTO = {
                key: sessionKey,
                roomType: roomTypes.room,
                name: roomContent.title,
                roomDescription: roomContent.description,
                members: session.get('members'),
            };

            if (editRoom) {
                dispatch(updateChatSession(sessionKey, sessionDTO));

                if (roomContent.attachment) {
                    dispatch(setRoomImage(sessionKey, roomContent.attachment));
                }
            } else {
                dispatch(
                    createRoomSession(
                        sessionDTO,
                        roomContent.attachment,
                        onSuccess,
                    ),
                );
            }
        }
    };

    const inputRef = useAutoFocus();

    return (
        <BaseModal
            open={true}
            header={header}
            onSubmit={handleSubmit}
            confirmButtonText={confirmButtonText}
            disableSubmit={!roomContent.title}
        >
            <div className="flex flex-col gap-5">
                <div className="flex items-center gap-x-3">
                    <div
                        className={twMerge(
                            'flex items-center justify-center rounded-2xl bg-gray-200',
                            !chosenExtensions.isEmpty() ? 'size-9' : 'size-14',
                        )}
                    >
                        {roomContent.imageSrc ? (
                            <img
                                src={roomContent.imageSrc}
                                alt={i18next.t('Chat.Room.Picture')}
                                className="h-full rounded-2xl object-cover"
                            />
                        ) : (
                            <Icon iconKey={'image'} className="text-gray-400" />
                        )}
                    </div>

                    <div
                        className={twMerge(
                            'cursor-pointer rounded-sm hover:bg-gray-300',
                            !chosenExtensions.isEmpty() ? null : 'bg-gray-200',
                        )}
                        onClick={() => fileInputRef.current.click()}
                    >
                        <input
                            ref={fileInputRef}
                            type="file"
                            className="hidden"
                            onChange={(e) => {
                                handleAttachment(e.target?.files[0]);
                                e.target.value = null;
                            }}
                            accept="image/*"
                        />
                        <p className="p-2 text-sm/4 font-medium">
                            {editRoom
                                ? i18next.t('Chat.Tooltip.Change.Image')
                                : i18next.t('Chat.Tooltip.Image')}
                        </p>
                    </div>
                </div>

                <div className="flex w-full flex-col gap-y-1.5">
                    <label htmlFor="title" className="text-xs text-gray-800">
                        {i18next.t('Chat.Public.Create.Name.Title')}
                    </label>
                    <BaseInput
                        onChange={(e) => {
                            const title = e.target?.value;

                            setRoomContent((prev) => ({
                                ...prev,
                                title,
                            }));
                        }}
                        value={roomContent.title}
                        ref={
                            inputRef as React.MutableRefObject<HTMLInputElement>
                        }
                        maxLength={32}
                        id="title"
                    />
                </div>

                <BaseTextArea
                    label={i18next.t('Chat.Public.Create.Description.Title')}
                    value={roomContent.description}
                    onChange={(e) => {
                        const description = e.target?.value;

                        setRoomContent((prev) => ({
                            ...prev,
                            description,
                        }));
                    }}
                    maxLength={255}
                    id="description"
                />

                {!chosenExtensions.isEmpty() && (
                    <p className="w-4/5 text-xs text-gray-500">
                        {i18next.t('chat.create_room_help')}
                    </p>
                )}
            </div>
        </BaseModal>
    );
};

export default ManageRoomModal;
